import { AppDataContext } from "modules/main/context/appData";
import { useCart } from "modules/cart/hooks/useCart";
import { useFetch } from "modules/main/hooks/useFetch";
import { useCallback, useContext, useEffect, useState} from "react";
import { endPointOptimizedResults } from "modules/main/services/endpoints";
import { OptimizedCartResponse, CartCreationStatus, CartItemStatus } from "modules/checkout/types/optimizedCart";
import { FetchDataOptions} from "modules/main/services/fetchData";

import { CartTableDetail } from "modules/checkout/components/cartDetailTable";
import { CartTableResume } from "modules/checkout/components/cartResumeTable";
import { AuthError } from "modules/authentication/types/form-login-types";
import { NoAuthError } from "modules/authentication/components/no-auth-error";
import { GeneralError } from "modules/main/components/general-error";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";


import { CartItem } from "modules/cart/types/cart";
import { useOptimizedCartUpdates } from "../hooks/useOptimizedScenarioUpdate";
import { getTotalsFromScenario } from "utils/scenarioUtils";

import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty"
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { ViewOrderSkeleton } from "modules/checkout/components/viewOrderSkeleton";
import posthog from "posthog-js";
import { PageBreadcrumbs } from "modules/main/components/page-breadcrumbs";
import { usePageTitle } from "hooks/usePageTitle";
import { useConnectedAccountsValidation } from "modules/checkout/hooks/useConnectedAccountsValidation";
import { ConnectedAccountsDialog } from "modules/checkout/components/ConnectedAccountsDialog";


const CREATE_CART_EVENT_CODE = "Crear carrito";

export const ViewOrder = () => {
    const {requireLoginAction} = useAuth();
    const {setBreadcrumb,setShowBack } = usePageTitle();
    const {cart, clearCart} = useCart();    
    const navigate = useNavigate();   
    const {
        isValidating,
        disconnectedStores,
        isDialogOpen,
        setIsDialogOpen,
        validateBeforeCreateCart,
    } = useConnectedAccountsValidation();
    const [recalculate, setRecalculate] = useState(false);    
    const {storeList} = useContext(AppDataContext); 
    const isDesktop = useMediaQuery(isDesktopSize);
    const [refresh, setRefresh] = useState(false);
    const [pendingCartContinuation, setPendingCartContinuation] = useState<(() => void) | null>(null);

    const [workingCart, setWorkingCart] = useState<CartItem[]>([]);    
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const requestOptions:FetchDataOptions = {
        method:"GET",
        headers:{
            "content-type":"application/json",
        }
    }

    const { order_id: orderIdParam, scenario_id: scenarioIdParam, store_id: storeIdParam } = useParams<{ order_id: string; scenario_id: string; store_id?: string }>();
    const order_id = Number.isFinite(Number(orderIdParam)) ? Number(orderIdParam) : 0;
    const scenario_id = Number.isFinite(Number(scenarioIdParam)) ? Number(scenarioIdParam) : 0;
    const store_id = Number.isFinite(Number(storeIdParam)) ? Number(storeIdParam) : undefined;

    const [cartStatus, setCartStatus] = useState<CartCreationStatus[]>([]);
    const {data,error,loading} = useFetch<OptimizedCartResponse>(endPointOptimizedResults(order_id, scenario_id, store_id),requestOptions,refresh);

    const [scenarios, setScenarios] = useState<OptimizedCartResponse["scenarios"]>([]);

    const [selectedIndexScenario, setSelectedIndexScenario] = useState(0);

    
    const { applyChanges: applyCartChanges, isApplyingChanges } = useOptimizedCartUpdates({
            orderId: order_id,
            selectedIndexScenario,
            onSuccess: (updatedData) => {
                setHasUnsavedChanges(false);
                // Actualizar workingCart con datos del servidor
                const serverCart = updatedData.scenarios[selectedIndexScenario].opt_cart.map(item => ({
                    ...item,
                    price_component: item.price_component.map(pc => ({...pc}))
                }));
                setWorkingCart(serverCart);
                setScenarios(updatedData.scenarios);
            },
            onError: (error) => {
                console.warn("Error aplicando cambios:", error.message);
                setHasUnsavedChanges(true);
            }
    });

    const applyChanges = () => applyCartChanges(scenarios[selectedIndexScenario].order_id??0, workingCart);
    
    const initializeWorkingArea = (scenarioIndex:number,scenariosData?: OptimizedCartResponse["scenarios"]) =>{
        const currentScenarios = scenariosData || scenarios;
        //Clone cart 
        if (currentScenarios && currentScenarios[scenarioIndex]) {
            const clonedCart = currentScenarios[scenarioIndex].opt_cart.map(item => ({
                ...item,
                price_component: item.price_component.map(pc => ({...pc}))
            }));
            setWorkingCart(clonedCart);
            setHasUnsavedChanges(false);
        }
    }

    const initializeCartStatus = useCallback((scenarioIndex: number, scenariosData?: OptimizedCartResponse["scenarios"]) => {
        const currentScenarios = scenariosData || scenarios;
        if (!currentScenarios || !currentScenarios[scenarioIndex]) return;

        const currentScenario = currentScenarios[scenarioIndex];
        const isUniqueStore = currentScenario.scenarioType === "UNIQUE_STORE";
        const { cheapestStoreId } = getTotalsFromScenario(currentScenario, isUniqueStore, false);

        const newCartStatus: CartCreationStatus[] = currentScenario.store_resume
                .filter(store=>((cheapestStoreId === store.id && isUniqueStore) || !isUniqueStore))
                .map(store => {
                    const productsForStore: CartItemStatus[] = currentScenario.opt_cart.reduce((acc, item) => {
                        const storeComponent = item.price_component.find(pc => pc.id === store.id && !pc.no_met_conditions);
                        if (storeComponent && storeComponent.quantity > 0) {
                            acc.push({
                                ...item,
                                price_component: item.price_component.map(pc => ({
                                    ...pc,
                                    checked: false
                                }))
                            });
                        }
                        return acc;
                    }, [] as CartItemStatus[]);

                    return {
                        store_id: store.id,
                        products: productsForStore
                    };
                });
                setCartStatus(newCartStatus);
    }, [scenarios]);
 


    useEffect(() => {
    if (data?.scenarios && data.scenarios.length > 0) {
        // Solo inicializar una vez al cargar los datos
        setScenarios(data.scenarios);
        const scenarioIndexFromPath = data.scenarios.findIndex((scenario) => scenario.order_id === scenario_id);
        const reccomendedIndex = Math.max(0, data.scenarios.findIndex((scenario) => scenario.reccomended));
        const initialIndex = scenarioIndexFromPath >= 0 ? scenarioIndexFromPath : reccomendedIndex;
        setSelectedIndexScenario(initialIndex);
        if(workingCart.length === 0){
            initializeWorkingArea(initialIndex, data.scenarios);
            initializeCartStatus(initialIndex, data.scenarios);
        }
        
    }
    }, [data, initializeCartStatus, scenario_id]);

    // The breadcrumb replaces the h2 title (HeaderBar precedence) and the
    // back arrow is hidden — the breadcrumb itself provides navigation.
    useEffect(() => {
      setBreadcrumb(
        <PageBreadcrumbs
          crumbs={[
            { label: "Inicio", to: "/store" },
            {
              label: "Tu Compra Optimizada",
              to: `/cart/results/${order_id}/`,
            },
            { label: "Detalle" },
          ]}
        />,
      );
      setShowBack(false);
      return () => setBreadcrumb(null);
    }, [order_id, setBreadcrumb]);

    const handleScenarioChange = useCallback((index: number, loading: boolean) => {
        
        if (!scenarios?.[index] || loading) return;
        
        const clonedCart = scenarios[index].opt_cart.map(item => ({
            ...item,
            price_component: item.price_component.map(pc => ({...pc}))
        }));
        setSelectedIndexScenario(index);
        setWorkingCart(clonedCart);
        setHasUnsavedChanges(false);
        initializeCartStatus(index);
    }, [scenarios, initializeCartStatus]);

    
    // Función para descartar cambios
    const resetWorkingCart = () => {
        initializeWorkingArea(selectedIndexScenario);
    };


    const continueToCartCreation = (scenarioId:number, storeId?:number) => {
        posthog.capture(CREATE_CART_EVENT_CODE);
        navigate('/cart/create_cart?scenario_id=' + scenarioId + (storeId ? `&store_id=${storeId}` : ""));
    }

    const onCreateCart = async (scenarioId:number, storeId?:number) => {
        
        if(!requireLoginAction()){            
            return;
        }
        setPendingCartContinuation(() => () => continueToCartCreation(scenarioId, storeId));
        const canNavigate = await validateBeforeCreateCart({ scenarioId, storeId });
        if (!canNavigate) {
            return;
        }

        setPendingCartContinuation(null);
        continueToCartCreation(scenarioId, storeId);
    }

    const handleConnectedAccountsOpenChange = (open: boolean) => {
        setIsDialogOpen(open);
        if (!open) {
            setPendingCartContinuation(null);
        }
    }

    const handleCreateCartsFromConnectedAccounts = () => {
        pendingCartContinuation?.();
        setPendingCartContinuation(null);
    }


    if(loading){
        return <ViewOrderSkeleton isDesktop={isDesktop} />;
    }

    if(error){
        const ErrorComponent = (error instanceof AuthError) ? NoAuthError : GeneralError;
        return (<><ErrorComponent/></>);
    }

    if(!data){
        return <ViewOrderSkeleton isDesktop={isDesktop} />;
    }
    
    if(scenarios.length === 0){
        return (<>
        <div className="w-full flex justify-center items-center min-h-[50dvh]">
            <Empty>
                <EmptyHeader>
                    <EmptyMedia variant="icon">
                    <ShoppingCart />
                    </EmptyMedia>
                    <EmptyTitle>Tu carrito aún es muy pequeño</EmptyTitle>
                    <EmptyDescription>
                    Todavía no alcanzas el monto mínimo para optimizar precios entre tiendas.
                    Agrega más productos y podrás ver cómo se distribuye tu compra para ahorrar.
                    </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                    <div className="flex gap-2">
                    <Link to="/">
                        <Button>Agregar más productos</Button>
                    </Link>                
                    </div>
                </EmptyContent>
            </Empty>
        </div>
        </>
    );
    }
    const tableResume = (
        <CartTableResume 
            mode = "RESULT"
            setSelectedIndexScenario={handleScenarioChange}
            selectedIndexScenario={selectedIndexScenario}
            optimizedScenarios={scenarios}
            storeId={store_id}
            currCart={cart}
            storeList={storeList}
            className="overflow-hidden min-w-[25rem]"
            isMobile={!isDesktop}
            loading={isApplyingChanges}
            refreshTrigger={()=>setRefresh(!refresh)}
            clearCart={clearCart}
            recalculate={recalculate}
            setRecalculate={setRecalculate}
            cartCreationStatus={cartStatus}
            createCartCallBack={onCreateCart}
            validationLoading={isValidating}
    />
    );
    
    return (
    <>    
    <div className="py-4 px-6">
        <div className={cn("flex w-full flex-col gap-2",{
            "w-full":!isDesktop,
            "lg:w-[80%] xl:w-[80%] 2xl:w-[75%] mx-auto":isDesktop,
        })}>
            <CartTableDetail 
                mode = "RESULT"
                selectedIndexScenario={selectedIndexScenario}
                optimizedScenarios={scenarios}
                loading={isApplyingChanges}
                validationLoading={isValidating}
                hideSavingsBadge
                errorCode={data?.error}
                currCart={cart}
                createCartCallBack={onCreateCart}
                addDeletefromCart={()=>{            
                }}
                setNoReplace ={(productId, newMethod) => {
                    // Actualizar el workingCart con el nuevo método
                    const updatedCart = workingCart.map(item => 
                        item.id === productId 
                            ? { ...item, method: newMethod }
                            : item
                    );
                    setWorkingCart(updatedCart);
                    setHasUnsavedChanges(true); // Marcar como cambios no guardados
                }}
                storeList={storeList}       
                className="flex-1"
                isMobile={!isDesktop}
                setSelectedIndexScenario={handleScenarioChange}
                workingCart={workingCart}
                setWorkingCart={setWorkingCart}
                hasUnsavedChanges={hasUnsavedChanges}
                applyChanges={applyChanges}
                resetWorkingCart={resetWorkingCart}       
                cartCreationStatus={cartStatus}     
                setCartStatus={setCartStatus}
            />
        </div>

    </div>
    {(!isDesktop)&&tableResume}
    <ConnectedAccountsDialog
        open={isDialogOpen}
        onOpenChange={handleConnectedAccountsOpenChange}
        disconnectedStores={disconnectedStores}
        onCreateCarts={handleCreateCartsFromConnectedAccounts}
    />
    </>
    );
}
